import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import { useElevation } from "@/components/noxn/AdminShared";
import { ShieldCheck, Loader2, Check, AlertTriangle, ListChecks } from "lucide-react";
import { fmtLocal, userTz } from "@/lib/time";
import { PricingTiersTab } from "@/pages/admin/PricingTiersTab";

const TABS = ["Tasks & Operations", "Finance", "Pricing (PPP)", "Roles & Permissions", "Audit log"];

export default function AdminConsole() {
    usePageMeta({ title: "Admin console · NOXN", description: "Tasks, roles and audit." });
    const { user } = useAuth();
    const [tab, setTab] = useState("Tasks & Operations");
    const [access, setAccess] = useState(null);
    const { withElevation, elevateUI } = useElevation();

    useEffect(() => {
        api.get("/admin/my-access").then(({ data }) => setAccess(data)).catch(() => setAccess({ permissions: [] }));
    }, []);

    const can = (p) => access?.permissions?.includes(p);

    return (
        <PortalShell title="Admin console" mainClassName="p-0">
        <div className="min-h-screen bg-[#0F1E3B] text-white" data-testid="admin-console">
            <main className="mx-auto max-w-6xl px-6 lg:px-10 py-10">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={16} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">
                        {access ? `Signed in as ${access.role}` : "…"}
                    </span>
                </div>
                <h1 className="font-display font-black tracking-tighter text-4xl">Administration.</h1>
                <div className="mt-8 flex items-center gap-2 border-b border-white/10">
                    {TABS.map((t) => (
                        <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.split(" ")[0].toLowerCase()}`}
                            className={`px-4 py-2.5 text-xs font-mono-tech uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === t ? "border-[#FF5A00] text-white" : "border-transparent text-slate-500 hover:text-white"}`}>
                            {t}
                        </button>
                    ))}
                </div>
                <div className="mt-8">
                    {tab === "Tasks & Operations" && <TasksAdminTab can={can} isSuper={access?.role === "super_admin"} tz={userTz(user)} />}
                    {tab === "Finance" && <FinanceTab can={can} />}
                    {tab === "Pricing (PPP)" && <PricingTiersTab withElevation={withElevation} />}
                    {tab === "Roles & Permissions" && <RolesTab can={can} withElevation={withElevation} />}
                    {tab === "Audit log" && <AuditTab can={can} tz={userTz(user)} />}
                </div>
            </main>
            {elevateUI}
        </div>
        </PortalShell>
    );
}

const RolesTab = ({ can, withElevation }) => {
    const [roles, setRoles] = useState(null);
    const [perms, setPerms] = useState([]);
    const load = () => api.get("/admin/roles").then(({ data }) => setRoles(data.roles));
    useEffect(() => { load(); api.get("/admin/permissions").then(({ data }) => setPerms(data.permissions)); }, []);
    if (!can("roles.manage")) return <div className="text-sm text-slate-500" data-testid="roles-no-access">You don't have permission to manage roles.</div>;
    if (!roles) return <Loader2 className="animate-spin text-[#FF5A00]" size={18} />;

    const toggle = (role, permKey) => withElevation(async () => {
        const has = role.permissions.includes(permKey);
        const permissions = has ? role.permissions.filter((p) => p !== permKey) : [...role.permissions, permKey];
        await api.post("/admin/roles", { key: role.key, name: role.name, permissions, data_scope: role.data_scope || "all" });
        load();
    });
    const toggleScope = (role) => withElevation(async () => {
        await api.post("/admin/roles", { key: role.key, name: role.name, permissions: role.permissions, data_scope: role.data_scope === "assigned" ? "all" : "assigned" });
        load();
    });

    const editable = roles.filter((r) => r.key !== "super_admin");
    return (
        <div className="overflow-x-auto border border-white/10" data-testid="roles-matrix">
            <table className="w-full text-xs">
                <thead><tr className="border-b border-white/10">
                    <th className="px-3 py-2 text-left font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">Permission</th>
                    {editable.map((r) => (
                        <th key={r.key} className="px-2 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-slate-400 whitespace-nowrap">
                            {r.name}
                            <button onClick={() => toggleScope(r)} data-testid={`role-scope-${r.key}`}
                                className={`block mx-auto mt-1 px-1.5 py-0.5 border text-[8px] ${r.data_scope === "assigned" ? "border-amber-400/50 text-amber-300" : "border-white/15 text-slate-500"}`}>
                                {r.data_scope === "assigned" ? "assigned only" : "all data"}
                            </button>
                        </th>
                    ))}
                </tr></thead>
                <tbody>
                    {perms.map((p) => (
                        <tr key={p.key} className="border-b border-white/5">
                            <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{p.label}</td>
                            {editable.map((r) => (
                                <td key={r.key} className="px-2 py-2 text-center">
                                    <button onClick={() => toggle(r, p.key)} data-testid={`perm-${r.key}-${p.key}`}
                                        className={`w-5 h-5 border inline-flex items-center justify-center ${r.permissions.includes(p.key) ? "border-[#FF5A00] bg-[#FF5A00]" : "border-white/20"}`}>
                                        {r.permissions.includes(p.key) && <Check size={12} className="text-white" strokeWidth={3} />}
                                    </button>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className="px-3 py-2 text-[10px] text-slate-500">Super Admin always has every permission. Changes apply instantly — no code changes needed.</p>
        </div>
    );
};

const AuditTab = ({ can, tz }) => {
    const [logs, setLogs] = useState(null);
    useEffect(() => { api.get("/admin/audit").then(({ data }) => setLogs(data.logs)).catch(() => setLogs([])); }, []);
    if (!can("audit.view")) return <div className="text-sm text-slate-500" data-testid="audit-no-access">You don't have permission to view audit logs.</div>;
    if (!logs) return <Loader2 className="animate-spin text-[#FF5A00]" size={18} />;
    return (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="audit-log-list">
            {logs.length === 0 && <div className="px-4 py-3 text-sm text-slate-500">No audit entries yet.</div>}
            {logs.map((l) => (
                <div key={l.id} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-mono-tech text-slate-500 w-48 shrink-0">{fmtLocal(l.at, tz)}</span>
                    <span className="text-[#FF5A00] font-mono-tech uppercase tracking-widest">{l.action}</span>
                    <span className="text-white">{l.target}</span>
                    <span className="text-slate-500 truncate max-w-md">{JSON.stringify(l.meta)}</span>
                </div>
            ))}
        </div>
    );
};

const FinanceTab = ({ can }) => {
    const [rules, setRules] = useState(null);
    const [saving, setSaving] = useState(false);
    const canManage = can("finance.manage");
    useEffect(() => {
        api.get("/contracts/meta/pricing-rules").then(({ data }) => setRules(data)).catch(() => setRules("denied"));
    }, []);
    if (rules === "denied" || !can("financials.view")) {
        return <div className="text-sm text-slate-500" data-testid="finance-no-access">Financial controls are restricted — Super Admin (manage) and Operations Manager (view) only.</div>;
    }
    if (!rules) return <Loader2 className="animate-spin text-[#FF5A00]" size={18} />;
    const save = async (updates) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/contracts/meta/pricing-rules", updates);
            setRules(data);
            toast.success("Pricing rules updated.");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not update pricing rules."); }
        finally { setSaving(false); }
    };
    return (
        <div className="max-w-2xl" data-testid="finance-tab">
            <section className="border border-white/10 p-6">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-1">/ Dynamic pricing rules</div>
                <p className="text-xs text-slate-500 mb-5">Applied automatically when a quote is revised. Per-screen peak multipliers are set on each screen in Inventory.</p>
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">Bulk threshold (booked days)</label>
                        <input type="number" min="1" max="365" defaultValue={rules.bulk_days_threshold} disabled={!canManage} data-testid="finance-bulk-threshold"
                            onBlur={(e) => canManage && parseInt(e.target.value, 10) !== rules.bulk_days_threshold && save({ bulk_days_threshold: parseInt(e.target.value, 10) })}
                            className="bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-sm w-36 disabled:opacity-50" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">Bulk discount %</label>
                        <input type="number" min="0" max="90" step="0.5" defaultValue={rules.bulk_discount_pct} disabled={!canManage} data-testid="finance-bulk-pct"
                            onBlur={(e) => canManage && parseFloat(e.target.value) !== rules.bulk_discount_pct && save({ bulk_discount_pct: parseFloat(e.target.value) })}
                            className="bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-sm w-32 disabled:opacity-50" />
                    </div>
                    <button onClick={() => canManage && save({ enabled: !rules.enabled })} disabled={!canManage || saving} data-testid="finance-rules-toggle"
                        className={`px-4 py-2 text-[10px] font-mono-tech uppercase tracking-widest border ${rules.enabled ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-slate-500"} disabled:opacity-50`}>
                        {rules.enabled ? "Rules enabled" : "Rules disabled"}
                    </button>
                </div>
                {!canManage && <p className="mt-4 text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">Read-only — only the Super Admin can change financial controls.</p>}
            </section>
            <p className="mt-4 text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">
                Quote revisions never overwrite — every version is kept. All amounts in AUD.
            </p>
        </div>
    );
};

const STATUS_CHIP = {
    pending: "border-sky-400/40 text-sky-300",
    in_progress: "border-amber-400/40 text-amber-300",
    blocked: "border-white/15 text-slate-500",
    completed: "border-emerald-400/40 text-emerald-300",
};

const TasksAdminTab = ({ can, isSuper, tz }) => {
    const [esc, setEsc] = useState(null);
    const [tasks, setTasks] = useState(null);
    const [roleF, setRoleF] = useState("");
    const [statusF, setStatusF] = useState("");

    const loadEsc = () => {
        if (!isSuper) { setEsc([]); return; }
        api.get("/tasks/escalations").then(({ data }) => setEsc(data.escalations)).catch(() => setEsc([]));
    };
    const loadTasks = () => {
        const params = {};
        if (roleF) params.role = roleF;
        if (statusF) params.status = statusF;
        api.get("/tasks/all", { params }).then(({ data }) => setTasks(data.tasks)).catch(() => setTasks([]));
    };
    useEffect(() => { loadEsc(); }, [isSuper]);
    useEffect(() => { loadTasks(); }, [roleF, statusF]);

    const complete = async (t) => {
        try {
            await api.patch(`/tasks/${t.id}`, { status: "completed" });
            toast.success("Task completed.");
            loadTasks(); loadEsc();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not complete task."); }
    };
    const setDeadline = async (t, deadline) => {
        try {
            await api.patch(`/tasks/${t.id}`, { deadline });
            toast.success("Deadline updated.");
            loadTasks(); loadEsc();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not update deadline."); }
    };

    if (!can("campaigns.manage")) return <div className="text-sm text-slate-500" data-testid="tasks-admin-no-access">You don't have permission to manage tasks.</div>;

    const roles = [...new Set((tasks || []).map((t) => t.role).filter(Boolean))].sort();
    return (
        <div className="space-y-8">
            {isSuper && (
                <section className="border border-red-400/30 bg-red-500/5 p-5" data-testid="admin-escalations-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={14} className="text-red-400" />
                        <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-red-300">Escalations — overdue tasks</span>
                        <span className="text-[10px] font-mono-tech text-slate-500">({esc ? esc.length : "…"})</span>
                    </div>
                    {!esc ? <Loader2 className="animate-spin text-[#FF5A00]" size={16} /> :
                        esc.length === 0 ? <div className="text-xs text-slate-500" data-testid="admin-escalations-empty">Nothing escalated — every task is on schedule.</div> : (
                            <div className="divide-y divide-white/5">
                                {esc.map((t) => (
                                    <div key={t.id} className="py-2.5 flex flex-wrap items-center gap-3 text-xs" data-testid={`admin-escalation-row-${t.key}-${t.campaign_id}`}>
                                        <span className="flex-1 min-w-[160px] text-white">{t.title}</span>
                                        <span className="text-slate-500 font-mono-tech">{t.business_name || t.campaign_id.slice(0, 8)}</span>
                                        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">{(t.role || "").replace(/_/g, " ")}</span>
                                        <span className="text-red-300 font-mono-tech" data-testid={`admin-escalation-deadline-${t.key}`}>due {t.deadline}</span>
                                        <span className="px-1.5 py-0.5 border border-red-400/40 text-red-300 text-[9px] font-mono-tech uppercase tracking-widest">{t.days_overdue}d overdue</span>
                                        <button onClick={() => complete(t)} data-testid={`admin-escalation-complete-${t.key}`}
                                            className="inline-flex items-center gap-1 bg-[#FF5A00] hover:bg-[#E65200] text-white px-2 py-1 text-[9px] font-semibold uppercase tracking-widest">
                                            <Check size={9} /> Resolve
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                </section>
            )}

            <section data-testid="admin-tasks-section">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <ListChecks size={14} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">All workflow tasks</span>
                    <select value={roleF} onChange={(e) => setRoleF(e.target.value)} data-testid="admin-tasks-role-filter"
                        className="bg-[#1B2E55] border border-white/10 px-2 py-1.5 text-xs text-white">
                        <option value="">All roles</option>
                        {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </select>
                    <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="admin-tasks-status-filter"
                        className="bg-[#1B2E55] border border-white/10 px-2 py-1.5 text-xs text-white">
                        <option value="">All statuses</option>
                        {["pending", "in_progress", "blocked", "completed"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                    <span className="text-[10px] font-mono-tech text-slate-600">({tasks ? tasks.length : "…"} tasks)</span>
                </div>
                {!tasks ? <Loader2 className="animate-spin text-[#FF5A00]" size={16} /> : (
                    <div className="overflow-x-auto border border-white/10" data-testid="admin-tasks-table">
                        <table className="w-full text-xs">
                            <thead><tr className="border-b border-white/10 text-left">
                                {["Task", "Campaign", "Role", "Status", isSuper ? "Deadline" : null, ""].filter((h) => h !== null).map((h) => (
                                    <th key={h} className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-slate-500">{h}</th>
                                ))}
                            </tr></thead>
                            <tbody>
                                {tasks.map((t) => (
                                    <tr key={t.id} className="border-b border-white/5" data-testid={`admin-task-row-${t.key}-${t.campaign_id}`}>
                                        <td className="px-3 py-2 text-white">{t.title}{t.escalated && t.status !== "completed" && <AlertTriangle size={11} className="inline ml-1.5 text-red-400" />}</td>
                                        <td className="px-3 py-2 text-slate-400 font-mono-tech">{t.business_name || t.campaign_id.slice(0, 8)}</td>
                                        <td className="px-3 py-2 text-slate-400 capitalize whitespace-nowrap">{(t.role || "").replace(/_/g, " ")}</td>
                                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 border text-[9px] font-mono-tech uppercase tracking-widest ${STATUS_CHIP[t.status] || "border-white/15 text-slate-500"}`}>{(t.status || "pending").replace("_", " ")}</span></td>
                                        {isSuper && (
                                            <td className="px-3 py-2">
                                                <input type="date" defaultValue={t.deadline} data-testid={`admin-task-deadline-${t.key}-${t.campaign_id}`}
                                                    onBlur={(e) => e.target.value && e.target.value !== t.deadline && setDeadline(t, e.target.value)}
                                                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                                                    className={`bg-transparent border border-white/10 px-1.5 py-1 text-[11px] font-mono-tech ${t.escalated && t.status !== "completed" ? "text-red-300" : "text-slate-300"}`} />
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-right">
                                            {t.status !== "completed" && t.status !== "blocked" && (
                                                <button onClick={() => complete(t)} data-testid={`admin-task-complete-${t.key}-${t.campaign_id}`}
                                                    className="text-slate-400 hover:text-emerald-300"><Check size={13} /></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {tasks.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-slate-500">No tasks match these filters.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="mt-2 text-[10px] text-slate-500">
                    {isSuper ? `Deadlines shown in each task's due date · your local time zone is ${tz}. Overdue tasks escalate here automatically every hour.`
                        : "Deadlines and escalations are visible to the Super Admin only."}
                </p>
            </section>
        </div>
    );
};
